import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Clock, Loader2, X, Check } from "lucide-react";
import { getOpenSlots, requestSlot } from "../lib/api";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function MonthCalendar() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selected, setSelected] = useState(null); // "YYYY-MM-DD"
  const [active, setActive] = useState(null); // slot for request modal
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = () => getOpenSlots().then((data) => {
    setSlots(data);
    if (data && data.length) {
      setCursor((cur) => {
        const curHas = data.some((s) => {
          const [sy, sm] = s.date.split("-").map(Number);
          return sy === cur.getFullYear() && sm === cur.getMonth() + 1;
        });
        if (curHas) return cur;
        const earliest = data.map((s) => s.date).sort()[0];
        const [y, m] = earliest.split("-").map(Number);
        return new Date(y, m - 1, 1);
      });
    }
  }).catch(() => setSlots([])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const byDate = useMemo(() => {
    const m = {};
    slots.forEach((s) => { (m[s.date] ||= []).push(s); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.start_time.localeCompare(b.start_time)));
    return m;
  }, [slots]);

  const grid = useMemo(() => {
    const year = cursor.getFullYear(), month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const startOffset = (first.getDay() + 6) % 7; // Monday-first
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    return cells;
  }, [cursor]);

  const todayStr = ymd(new Date());
  const daySlots = selected ? (byDate[selected] || []) : [];

  const openRequest = (slot) => { setForm({ name: "", email: "", phone: "", note: "" }); setActive(slot); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required."); return; }
    setSaving(true);
    try {
      await requestSlot(active.id, form);
      setActive(null);
      toast.success("Request sent! We'll confirm your session by email.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Request failed. Try another time.";
      toast.error(typeof msg === "string" ? msg : "Request failed.");
      if (err?.response?.status === 409) load();
    } finally { setSaving(false); }
  };

  const inputClass = "w-full bg-transparent border-b border-[#3a3a3a] focus:border-gold py-3 text-white placeholder:text-zinc-600 outline-none transition-colors";

  return (
    <div data-testid="month-calendar">
      {!loading && slots.length === 0 && (
        <div className="mb-8 border border-[#27272a] bg-[#0e0e0e] p-5 text-center" data-testid="no-slots-banner">
          <p className="font-display text-lg text-white">No sessions are open right now</p>
          <p className="text-zinc-500 text-sm mt-1">New studio slots are added regularly — please check back soon or send us a message.</p>
        </div>
      )}
      <div className="grid lg:grid-cols-12 gap-8">
        {/* Calendar */}
        <div className="lg:col-span-7">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-2xl sm:text-3xl font-semibold">
              {MONTHS[cursor.getMonth()]} <span className="text-zinc-500">{cursor.getFullYear()}</span>
            </h3>
            <div className="flex gap-2">
              <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="h-10 w-10 grid place-items-center border border-[#3a3a3a] hover:border-gold hover:text-gold transition-colors" data-testid="cal-prev" aria-label="Previous month"><ChevronLeft size={18} /></button>
              <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="h-10 w-10 grid place-items-center border border-[#3a3a3a] hover:border-gold hover:text-gold transition-colors" data-testid="cal-next" aria-label="Next month"><ChevronRight size={18} /></button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center gap-3 text-zinc-500"><Loader2 className="animate-spin text-gold" size={18} /> Loading availability…</div>
          ) : (
            <>
              <div className="grid grid-cols-7 gap-1.5 mb-2">
                {DOW.map((d) => (<span key={d} className="text-center font-mono text-[10px] uppercase tracking-[0.1em] text-zinc-500 py-1">{d}</span>))}
              </div>
              <div className="grid grid-cols-7 gap-1.5">
                {grid.map((date, i) => {
                  if (!date) return <div key={i} />;
                  const ds = ymd(date);
                  const has = !!byDate[ds]?.length;
                  const isPast = ds < todayStr;
                  const isSel = ds === selected;
                  return (
                    <button
                      key={i}
                      disabled={!has || isPast}
                      onClick={() => setSelected(ds)}
                      data-testid={has && !isPast ? `cal-day-${ds}` : undefined}
                      className={`aspect-square flex flex-col items-center justify-center border text-sm transition-all duration-200
                        ${isSel ? "border-gold bg-gold text-black font-semibold" : "border-[#1e1e1e]"}
                        ${has && !isPast && !isSel ? "border-[#3a3a3a] text-white hover:border-gold hover:text-gold cursor-pointer" : ""}
                        ${!has || isPast ? "text-zinc-700 cursor-not-allowed" : ""}`}
                    >
                      {date.getDate()}
                      {has && !isPast && (<span className={`mt-1 h-1 w-1 rounded-full ${isSel ? "bg-black" : "bg-gold"}`} />)}
                    </button>
                  );
                })}
              </div>
              <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.12em] text-zinc-500">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-gold mr-2 align-middle" />
                Days with available 1-hour slots
              </p>
            </>
          )}
        </div>

        {/* Times panel */}
        <div className="lg:col-span-5">
          <div className="border border-[#27272a] bg-[#0e0e0e] p-6 lg:p-7 min-h-[300px]">
            {!selected ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-10">
                <Clock className="text-gold mb-4" size={26} />
                <p className="font-display text-xl">Pick a date</p>
                <p className="text-zinc-500 text-sm mt-2">Select a highlighted day to see the available 1-hour slots.</p>
              </div>
            ) : (
              <>
                <h4 className="font-display text-xl font-semibold mb-1">
                  {new Date(selected + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
                </h4>
                <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-zinc-500 mb-5">
                  {daySlots.length} slot{daySlots.length !== 1 ? "s" : ""} available
                </p>
                <div className="grid grid-cols-2 gap-3" data-testid="day-slots">
                  {daySlots.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => openRequest(s)}
                      className="group text-left border border-[#3a3a3a] hover:border-gold hover:bg-[#141414] p-3.5 transition-colors"
                      data-testid={`cal-slot-${s.id}`}
                    >
                      <span className="inline-flex items-center gap-1.5 font-mono text-sm text-white"><Clock size={13} className="text-gold" /> {s.start_time}</span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.1em] text-zinc-500 mt-1 truncate">{s.setup}</span>
                    </button>
                  ))}
                </div>
                <p className="text-zinc-500 text-xs mt-5">Requests are reviewed and confirmed by our team — you'll get an email once approved.</p>
              </>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] grid place-items-center p-5 bg-black/80 backdrop-blur-sm"
            onClick={() => !saving && setActive(null)}
            data-testid="request-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-[#121212] border border-white/10 p-7 sm:p-9"
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">Request Session</span>
                  <h3 className="font-display text-2xl font-semibold mt-1">{active.setup}</h3>
                  <p className="font-mono text-xs text-zinc-400 mt-2">
                    {new Date(active.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} · {active.start_time} · 1 hour
                  </p>
                </div>
                <button onClick={() => !saving && setActive(null)} className="text-zinc-500 hover:text-white" data-testid="request-modal-close" aria-label="Close"><X size={22} /></button>
              </div>
              <form onSubmit={submit} className="space-y-5">
                <input className={inputClass} placeholder="Full name *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="request-name" />
                <input type="email" className={inputClass} placeholder="Email *" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="request-email" />
                <input className={inputClass} placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="request-phone" />
                <textarea rows={3} className={`${inputClass} resize-none`} placeholder="Anything we should know? (optional)" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="request-note" />
                <p className="text-zinc-500 text-xs">Nothing is locked until we approve your request — we'll confirm by email.</p>
                <button type="submit" disabled={saving} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-black font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 font-semibold hover:bg-[#d59e19] transition-colors disabled:opacity-60" data-testid="request-submit">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <><Check size={15} /> Send Request</>}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
